import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, Button, Badge } from '@b2b/ui-components';
import { History, Download, FileText } from 'lucide-react';
import { rfqService } from '@b2b/services';
import { QuoteRevision } from '@b2b/services/api/modules/rfq/rfq.types';
import { formatDistanceToNow } from 'date-fns';

interface QuoteRevisionHistoryProps {
  rfqId: string;
  quoteId: string;
  onRevise?: () => void;
}

export const QuoteRevisionHistory: React.FC<QuoteRevisionHistoryProps> = ({
  rfqId,
  quoteId,
  onRevise,
}) => {
  const [revisions, setRevisions] = useState<QuoteRevision[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchRevisions();
  }, [quoteId]);

  const fetchRevisions = async () => {
    try {
      setLoading(true);
      const response = await rfqService.getQuoteRevisions(rfqId, quoteId);
      setRevisions(response || []);
    } catch (error) {
      console.error('Error fetching revisions:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6 text-center text-muted-foreground">
          Loading revision history...
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <History className="w-4 h-4" />
          Revision History
        </CardTitle>
        {onRevise && (
          <Button size="sm" variant="outline" onClick={onRevise}>
            Revise Quote
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {revisions.length === 0 ? (
          <p className="text-muted-foreground text-sm">No revisions yet</p>
        ) : (
          <div className="space-y-4">
            {revisions.map((revision, index) => (
              <div
                key={revision.id}
                className="border-l-2 border-primary pl-4 pb-4 last:border-l-0 last:pb-0"
              >
                <div className="flex items-center justify-between mb-2">
                  <Badge variant="outline">Revision {revision.revisionIndex}</Badge>
                  <span className="text-xs text-muted-foreground">
                    {formatDistanceToNow(new Date(revision.createdAt), { addSuffix: true })}
                  </span>
                </div>
                <div className="space-y-1 text-sm">
                  <div>
                    <span className="text-muted-foreground">Price: </span>
                    <span className="font-medium">
                      {revision.currency} {Number(revision.price).toLocaleString()}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Lead Time: </span>
                    <span className="font-medium">{revision.leadTimeDays} days</span>
                  </div>
                  {revision.message && (
                    <div className="mt-2 text-muted-foreground">{revision.message}</div>
                  )}
                  {revision.attachments && revision.attachments.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {revision.attachments.map((attachmentId, idx) => (
                        <Button
                          key={idx}
                          variant="ghost"
                          size="sm"
                          className="h-6 text-xs"
                          onClick={() => {
                            window.open(`/api/media/${attachmentId}`, '_blank');
                          }}
                        >
                          <FileText className="w-3 h-3 mr-1" />
                          Attachment {idx + 1}
                        </Button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

